// ─── AURA como app instalable (PWA) ──────────────────────────────────────────
// Cuatro cosas, todas pequeñas y todas pensadas para el teléfono:
//
//  1. Registrar el service worker (src/pwa/sw.js) que guarda la carcasa para
//     que la app abra rápido y abra ALGO sin red. Solo en https: en el servidor
//     de pruebas y en local no hay worker, y la app funciona exactamente igual.
//  2. Decir cuando no hay conexión. El ERP no funciona sin nube; callárselo al
//     usuario es dejarlo trabajar en un documento que no se va a guardar.
//  3. Reaplicar el layout al PLEGAR o desplegar el teléfono (Galaxy Fold: la
//     pantalla externa es un celular angosto y la interna casi una tablet). La
//     capa móvil se decide por el ancho al pintar; al cruzar el umbral hay que
//     volver a pintar.
//  4. Ofrecer la instalación: en Android con el aviso nativo del navegador; en
//     iPhone con la instrucción (Compartir → Añadir a pantalla de inicio),
//     porque Safari no tiene aviso propio.
import { C } from "../core/config.js";
import { state } from "../core/state.js";
import { PRIORIDAD, anclar, desanclar } from "./dock.js";
import { render } from "./app.js";
import { esAppInstalada } from "./visorDoc.js";

const UMBRAL_MOVIL = 768;
const CLAVE_INSTALAR_NO = "erp:pwa_instalar_no";   // "ahora no": se vuelve a ofrecer a la semana
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export function iniciarPWA() {
  if (esAppInstalada()) document.documentElement.classList.add("aura-instalada");
  registrarWorker();
  vigilarConexion();
  vigilarPlegado();
  ofrecerInstalar();
}

function registrarWorker() {
  if (!("serviceWorker" in navigator) || location.protocol !== "https:") return;
  // Ruta relativa: el worker queda con el alcance de la carpeta donde vive el
  // index, así que una instalación de marca blanca bajo una subcarpeta también
  // funciona.
  navigator.serviceWorker.register("sw.js").catch(err => console.warn("PWA: no se pudo registrar el worker:", err && err.message));
}

// ── Sin conexión ─────────────────────────────────────────────────────────────
let avisoRed = null;
export function mostrarSinConexion() {
  if (avisoRed) return;
  avisoRed = document.createElement("div");
  avisoRed.id = "aviso-sin-conexion";
  avisoRed.style.cssText = `
    width:330px;max-width:calc(100vw - 32px);
    background:#7f1d1d;color:white;border-radius:10px;padding:11px 13px;
    box-shadow:0 12px 34px rgba(15,23,42,.25);font-family:inherit;
  `;
  avisoRed.innerHTML = `
    <div style="display:flex;gap:9px;align-items:flex-start;">
      <div style="font-size:18px;line-height:1;">📡</div>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;font-weight:800;">Sin conexión</div>
        <div style="font-size:10.5px;opacity:.9;margin-top:3px;line-height:1.45;">
          Lo que cambies ahora <b>no llega a la nube</b>. Espera a que vuelva la señal antes de guardar.
        </div>
      </div>
    </div>`;
  anclar(avisoRed, PRIORIDAD.seguridad);
}
export function ocultarSinConexion() {
  if (!avisoRed) return;
  desanclar(avisoRed);
  avisoRed = null;
}
function vigilarConexion() {
  window.addEventListener("offline", mostrarSinConexion);
  window.addEventListener("online", ocultarSinConexion);
  if (navigator.onLine === false) mostrarSinConexion();
}

// ── Plegar / desplegar ───────────────────────────────────────────────────────
let eraMovil = null;
let temporizadorResize = null;
export function esMovil() { return window.innerWidth <= UMBRAL_MOVIL; }
function vigilarPlegado() {
  eraMovil = esMovil();
  const revisar = () => {
    clearTimeout(temporizadorResize);
    temporizadorResize = setTimeout(() => {
      const ahora = esMovil();
      if (ahora === eraMovil) return;
      eraMovil = ahora;
      // Al pasar a pantalla grande el cajón lateral no tiene sentido abierto.
      state.mobSidebarOpen = false;
      // Solo si la app ya está pintada (hay sesión): en el login no hay nada
      // que reorganizar y render() no debe correr antes de tiempo.
      if (document.getElementById("main-layout")) render();
    }, 150);
  };
  window.addEventListener("resize", revisar);
  window.addEventListener("orientationchange", revisar);
}

// ── Instalar ─────────────────────────────────────────────────────────────────
let eventoInstalar = null;
let tarjetaInstalar = null;

function snoozeado() {
  try {
    const t = Number(localStorage.getItem(CLAVE_INSTALAR_NO) || 0);
    return t && (Date.now() - t) < SNOOZE_MS;
  } catch { return false; }
}
function posponer() {
  try { localStorage.setItem(CLAVE_INSTALAR_NO, String(Date.now())); } catch { /* sin almacenamiento */ }
  if (tarjetaInstalar) { desanclar(tarjetaInstalar); tarjetaInstalar = null; }
}

function esIOS() {
  const ua = navigator.userAgent || "";
  // iPadOS se presenta como Mac; el toque lo delata.
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

function tarjeta(cuerpoHTML) {
  const el = document.createElement("div");
  el.id = "aviso-instalar";
  el.style.cssText = `
    width:330px;max-width:calc(100vw - 32px);
    background:white;border:1px solid ${C.border};border-left:4px solid ${C.mathiz};
    border-radius:10px;box-shadow:0 12px 34px rgba(15,23,42,.18);padding:12px 13px;
    font-family:inherit;
  `;
  el.innerHTML = cuerpoHTML;
  return el;
}

function mostrarOfertaAndroid() {
  tarjetaInstalar = tarjeta(`
    <div style="display:flex;gap:9px;align-items:flex-start;">
      <img src="${window.ISOTIPO || ""}" alt="" style="width:34px;height:34px;border-radius:8px;flex-shrink:0;"/>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;font-weight:800;color:${C.text};">Instala AURA en tu teléfono</div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:3px;line-height:1.45;">Queda como una app más, a pantalla completa y con su ícono.</div>
        <div style="display:flex;gap:8px;margin-top:9px;">
          <button data-instalar-si style="flex:1;padding:8px 10px;background:${C.mathiz};color:white;border:none;border-radius:7px;font-weight:700;font-size:12px;cursor:pointer;">Instalar</button>
          <button data-instalar-no style="padding:8px 10px;background:#f1f5f9;color:${C.text};border:1px solid ${C.border};border-radius:7px;font-weight:600;font-size:12px;cursor:pointer;">Ahora no</button>
        </div>
      </div>
    </div>`);
  tarjetaInstalar.querySelector("[data-instalar-no]").onclick = posponer;
  tarjetaInstalar.querySelector("[data-instalar-si]").onclick = async () => {
    const ev = eventoInstalar;
    eventoInstalar = null;
    if (tarjetaInstalar) { desanclar(tarjetaInstalar); tarjetaInstalar = null; }
    if (!ev) return;
    try { ev.prompt(); await ev.userChoice; } catch { /* el usuario cerró el aviso */ }
  };
  anclar(tarjetaInstalar, PRIORIDAD.version);
}

function mostrarInstruccionIOS() {
  tarjetaInstalar = tarjeta(`
    <div style="display:flex;gap:9px;align-items:flex-start;">
      <img src="${window.ISOTIPO || ""}" alt="" style="width:34px;height:34px;border-radius:8px;flex-shrink:0;"/>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;font-weight:800;color:${C.text};">Instala AURA en tu iPhone</div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:3px;line-height:1.5;">
          En Safari toca <b>Compartir</b> <span style="font-size:13px;">⎋</span> y luego <b>«Añadir a pantalla de inicio»</b>. Queda como una app más, a pantalla completa.
        </div>
        <div style="display:flex;gap:8px;margin-top:9px;">
          <button data-instalar-no style="padding:7px 10px;background:#f1f5f9;color:${C.text};border:1px solid ${C.border};border-radius:7px;font-weight:600;font-size:12px;cursor:pointer;">Entendido</button>
        </div>
      </div>
    </div>`);
  tarjetaInstalar.querySelector("[data-instalar-no]").onclick = posponer;
  anclar(tarjetaInstalar, PRIORIDAD.version);
}

// La oferta espera a que la app esté ADENTRO (con sesión y layout pintado):
// en la pantalla de login estorba y nadie instala lo que todavía no usa.
function cuandoHayaApp(fn, intentos = 40) {
  if (document.getElementById("main-layout")) { fn(); return; }
  if (intentos <= 0) return;
  setTimeout(() => cuandoHayaApp(fn, intentos - 1), 1500);
}

function ofrecerInstalar() {
  if (esAppInstalada()) return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();            // el navegador no lo muestra solo: se ofrece desde la tarjeta
    eventoInstalar = e;
    if (snoozeado() || tarjetaInstalar) return;
    cuandoHayaApp(() => { if (eventoInstalar && !tarjetaInstalar) mostrarOfertaAndroid(); });
  });
  window.addEventListener("appinstalled", () => {
    if (tarjetaInstalar) { desanclar(tarjetaInstalar); tarjetaInstalar = null; }
    document.documentElement.classList.add("aura-instalada");
  });
  if (esIOS() && esMovil() && !snoozeado()) {
    cuandoHayaApp(() => { if (!tarjetaInstalar && !esAppInstalada()) mostrarInstruccionIOS(); });
  }
}
